import { useEffect, useRef } from 'react'
import { useAccountStore } from '@/stores/account-store'
import { useUpdateUserMutation } from '@/services/api/users-api'
import { useFilingViewerMode, type FilingViewerMode } from './use-filing-viewer-mode'

const MODES: readonly FilingViewerMode[] = ['theme', 'original', 'paper']

function isMode(value: unknown): value is FilingViewerMode {
  return typeof value === 'string' && (MODES as readonly string[]).includes(value)
}

/**
 * Two-way sync of the filing reading mode with the user's profile
 * (`filingReadingMode`), so the choice follows the user across devices.
 * localStorage stays the fast local cache in useFilingViewerMode; this layers
 * the server on top, exactly as useThemeServerSync does for the theme.
 *
 * - On first profile load: apply the saved mode (server wins on login).
 * - On later local changes: save the new mode (deduped).
 * - A profile with no saved mode triggers NO write on login.
 *
 * Mount once inside the authenticated tree (see AppLayout).
 */
export function useFilingReadingModeServerSync(): void {
  const { mode, setMode } = useFilingViewerMode()
  const user = useAccountStore((s) => s.user)
  const [updateUser] = useUpdateUserMutation()
  const initialized = useRef(false)
  const lastSynced = useRef<FilingViewerMode | null>(null)

  useEffect(() => {
    if (!user) return

    if (!initialized.current) {
      initialized.current = true
      const saved = isMode(user.filingReadingMode) ? user.filingReadingMode : null
      lastSynced.current = saved ?? mode
      // Return BEFORE the push path: setMode re-runs this effect with the
      // applied mode, and this commit's `mode` is still stale.
      if (saved && saved !== mode) setMode(saved)
      return
    }

    if (mode !== lastSynced.current) {
      lastSynced.current = mode
      void updateUser({ filingReadingMode: mode })
    }
  }, [user, mode, setMode, updateUser])
}
